import React, { useMemo, useState } from 'react';
import Plot from 'react-plotly.js';
import { CloudWatchRecord, AggregationType } from './types';
import moment from 'moment';
import { 
  Select, 
  SpaceBetween,
  FormField,
  Box
} from '@amzn/awsui-components-react';

import {
  COLOR_PALETTE,
  AGGREGATION_TIME_OPTIONS,
  AGGREGATION_TYPE_OPTIONS
} from './constants';

const aggregateDataByInterval = (
  data: CloudWatchRecord[],
  intervalSeconds: number,
  aggregationType: AggregationType
): CloudWatchRecord[] => {
  if (intervalSeconds === 0) {
    return [...data].sort((a, b) => 
      moment(a.timestamp).valueOf() - moment(b.timestamp).valueOf()
    );
  }

  const groups = new Map<string, CloudWatchRecord[]>();
  
  data.forEach(record => {
    const recordTime = moment(record.timestamp);
    let intervalStart;

    if (intervalSeconds >= 86400) { 
      intervalStart = moment(recordTime).startOf('day');
    } else if (intervalSeconds >= 3600) { 
      const hourGroup = Math.floor(recordTime.hour() / (intervalSeconds / 3600));
      intervalStart = moment(recordTime)
        .startOf('day')
        .add(hourGroup * (intervalSeconds / 3600), 'hours');
    } else { 
      const minutesSinceStartOfDay = recordTime.hours() * 60 + recordTime.minutes();
      const intervalGroup = Math.floor((minutesSinceStartOfDay * 60) / intervalSeconds);
      intervalStart = moment(recordTime)
        .startOf('day')
        .add(intervalGroup * intervalSeconds, 'seconds');
    }
    
    const key = intervalStart.format('YYYY-MM-DD HH:mm:ss.SSS');
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key)!.push(record);
  });

  const result: CloudWatchRecord[] = [];
  
  groups.forEach((groupRecords, timestamp) => {
    const aggregated: CloudWatchRecord = { timestamp };
    
    const numericFields = Object.keys(groupRecords[0]).filter(
      key => typeof groupRecords[0][key] === 'number'
    );

    numericFields.forEach(field => {
      const values = groupRecords.map(r => r[field] as number);
      
      switch (aggregationType) {
        case 'sum':
          aggregated[field] = values.reduce((a, b) => a + b, 0);
          break;
        case 'avg':
          aggregated[field] = Number((values.reduce((a, b) => a + b, 0) / values.length).toFixed(2));
          break;
        case 'max':
          aggregated[field] = Math.max(...values);
          break;
        case 'min':
          aggregated[field] = Math.min(...values);
          break;
        case 'count':
          aggregated[field] = values.length;
          break;
      }
    });

    result.push(aggregated);
  });

  return result.sort((a, b) => 
    moment(a.timestamp).valueOf() - moment(b.timestamp).valueOf()
  );
};

interface VisualizationViewProps {
  data: CloudWatchRecord[];
  selectedTimeZone?: {
    inputDisplay: string;
    label: string;
    value: string;
    code: string;
  };
}

export const VisualizationView: React.FC<VisualizationViewProps> = ({ 
  data,
  selectedTimeZone 
}) => {
  const [aggregationInterval, setAggregationInterval] = useState<number>(0);
  const [aggregationType, setAggregationType] = useState<AggregationType>('sum');

  const convertToTimezone = (timestamp: string) => {
    if (!selectedTimeZone) return timestamp;
    
    const tz = selectedTimeZone.value;
    const date = new Date(timestamp);
    
    if (tz === 'Browser Timezone') {
      return date.toLocaleString('en-US', {
        hour12: false,
        timeZoneName: 'short',
      });
    }
    
    return date.toLocaleString('en-US', {
      timeZone: tz,
      hour12: false,
    }) + ' ' + selectedTimeZone.code;
  };

  const aggregatedData = useMemo(() => {
    const result = aggregateDataByInterval(data, aggregationInterval, aggregationType);
    return result.map(item => ({
      ...item,
      timestamp: convertToTimezone(item.timestamp)
    }));
  }, [data, aggregationInterval, aggregationType, selectedTimeZone]);

  const numericFields = useMemo(() => {
    const fields = new Set<string>();
    data.forEach(record => {
      Object.entries(record).forEach(([key, value]) => {
        if (typeof value === 'number' && key !== 'timestamp') {
          fields.add(key);
        }
      });
    });
    return Array.from(fields);
  }, [data]);

  const plotData = numericFields.map((field, index) => ({
    x: aggregatedData.map(item => new Date(item.timestamp)),
    y: aggregatedData.map(item => (item[field] as number) || 0),
    type: 'scatter' as const,
    mode: 'lines+markers' as const,
    name: field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1'),
    line: {
      color: COLOR_PALETTE[index % COLOR_PALETTE.length],
      width: 2,
      shape: 'linear' as const
    },
    marker: {
      size: 6,
      color: COLOR_PALETTE[index % COLOR_PALETTE.length],
    }
  }));

  return (
    <Box padding="l">
      <SpaceBetween size="l">
        <SpaceBetween direction="horizontal" size="xs">
          <FormField label="Time Interval">
            <Select
              options={AGGREGATION_TIME_OPTIONS}
              selectedOption={
                AGGREGATION_TIME_OPTIONS.find(opt => 
                  parseInt(opt.value as string) === aggregationInterval
                ) || AGGREGATION_TIME_OPTIONS[0]
              }
              onChange={({ detail }) => {
                const newInterval = parseInt(detail.selectedOption.value as string);
                setAggregationInterval(newInterval);
              }}
            />
          </FormField>
          <FormField label="Aggregation Type">
            <Select
              options={AGGREGATION_TYPE_OPTIONS}
              selectedOption={
                AGGREGATION_TYPE_OPTIONS.find(opt => 
                  opt.value === aggregationType
                ) || AGGREGATION_TYPE_OPTIONS[0]
              }
              onChange={({ detail }) => {
                const newType = detail.selectedOption.value as AggregationType;
                setAggregationType(newType);
              }}
            />
          </FormField>
        </SpaceBetween>
        <Plot
          data={plotData}
          layout={{
            height: 400,
            xaxis: { 
              showgrid: false,
              title: 'Timestamp',
              tickformat: '%Y-%m-%d %H:%M:%S',
              zeroline: false,
              type: 'date',
              autorange: true,
              tickformatstops: [{
                dtickrange: [null, null],
                value: '%Y-%m-%d %H:%M:%S' + (selectedTimeZone?.code ? ` ${selectedTimeZone.code}` : '')
              }]
            },
            yaxis: {
              title: 'Values',
              showgrid: true,
              gridcolor: '#333333',
              gridwidth: 1,
              zeroline: false,
              autorange: true
            },
            showlegend: true,
            legend: {
              bgcolor: 'transparent',
              orientation: 'h',
              y: 1.1,
              xanchor: 'left',
              x: 0
            },
            margin: {
              l: 60,
              r: 20,
              t: 30,
              b: 40,
            },
            hovermode: 'x unified'
          }}
          config={{
            responsive: true,
            displayModeBar: true,
            scrollZoom: true,
            displaylogo: false,
            modeBarButtonsToRemove: [
              'lasso2d', 'select2d', 'sendDataToCloud',
              'zoom2d', 'pan2d', 'zoomIn2d', 'zoomOut2d',
              'autoScale2d', 'resetScale2d',
              'hoverClosestCartesian', 'hoverCompareCartesian',
            ]
          }}
          style={{ width: '100%' }}
        />
      </SpaceBetween>
    </Box>
  );
};